import { Injectable } from "@angular/core";
import { interval, map, takeUntil } from "rxjs";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { localize } from "@gtmhub/localization";
import { CloneRequest } from "@gtmhub/okrs/components/clone-okrs/clone-or-move.types";
import { OKRsEventType } from "@gtmhub/okrs/events";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { UiNotificationService } from "@webapp/ui/notification/services/notification.service";
import {
  CloneOrMoveResourceType,
  ICloneJob,
  IGoalCloneSelected,
  IGoalMoveSelected,
  IMetricCloneSelected,
  JobId,
  JobStatus,
  MoveResourceRequest,
  NotificationInfo,
} from "./clone-or-move.types";
import { CloneResourceRequest } from "./services/clone-okrs/clone-okrs-facade.models";
import { CloneOkrsFacade } from "./services/clone-okrs/clone-okrs-facade.service";
import { MoveOkrsFacade } from "./services/move-okrs/move-okrs-facade.service";

const goalsFieldsV2 =
  "metrics{id,name,attainment,ownerIds,manualType,goalId,customFields,actual,format,links{spec},cascadeType,sourceMetricId,targetMetricIds,orderId,confidence}," +
  "parentId,parentType,ownerIds,description," +
  "name,dateFrom,dateTo,childrenCount,fullSubTreeCount,metricsCount,attainment,attainmentTypeString,fullAggregatedAttainment,sessionId," +
  "tags{id,name,title,isActive,itemsTaggedCount},customFields,obfuscated,private,parentGoalSessionId,locked,workflow{status},orderId,designScore{totalPoints}";
const notificationPlacement = "bottomRight";
const sixSecondsInMilliseconds = 6000;
const fiveMinutesInMilliseconds = 300000;

@Injectable({
  providedIn: "root",
})
export class CloneOrMoveResourceService {
  public intervalTime = 3000;

  constructor(
    private uiErrorHandlingService: UIErrorHandlingService,
    private goalsFacade: GoalsFacade,
    private metricsFacade: MetricsFacade,
    private notificationService: UiNotificationService,
    private cloneOkrFacade: CloneOkrsFacade,
    private moveOkrsFacade: MoveOkrsFacade,
    private broadcastService: BroadcastService,
    private destroyService$: DestroyService
  ) {}

  public cloneSession(cloneRequest: CloneRequest): void {
    const payload: CloneResourceRequest = {
      type: CloneOrMoveResourceType.Session,
      resourceId: cloneRequest.source,
      targetType: CloneOrMoveResourceType.Session,
      targetResourceId: cloneRequest.target.sessionId,
      cloneChildren: !cloneRequest.target.cloneOnlyParentObjectives,
      cloneTasks: cloneRequest.target.cloneTasks,
      cloneTasksStatus: true,
      resetProgress: cloneRequest.target.resetProgress,
      // for session cloning the message is "Do not send notifications" when ticked
      sendNotifications: !cloneRequest.target.sendNotifications,
      keepCrossSessionRelationships: cloneRequest.target.shouldAlignSession,
    };
    this.cloneInternal(payload, {
      targetSessionName: cloneRequest.target.title,
    });
  }

  public cloneGoal(source: IGoal, cloneOptions: IGoalCloneSelected): void {
    const targetType: CloneOrMoveResourceType = cloneOptions.goal ? CloneOrMoveResourceType.Objective : CloneOrMoveResourceType.Session;
    const targetResourceId = cloneOptions.goal ? cloneOptions.goal.id : cloneOptions.session.id;
    const payload: CloneResourceRequest = {
      type: CloneOrMoveResourceType.Objective,
      resourceId: source.id,
      targetType: targetType,
      targetResourceId: targetResourceId,
      cloneChildren: cloneOptions.cloneChildren,
      cloneTasks: cloneOptions.cloneTasks,
      cloneTasksStatus: true,
      resetProgress: !cloneOptions.progress,
      sendNotifications: cloneOptions.notifications,
      duplicateInsights: cloneOptions.duplicateInsight,
    };
    // you can select goal target and then select child session of the selected goal.
    // in that case we need to send additional parameter targetSessionId with the selection so you can keep the link
    if (cloneOptions.session && cloneOptions.goal) {
      payload.targetSessionId = cloneOptions.session.id;
    }

    this.cloneInternal(payload, { targetSessionName: cloneOptions.session.title });
  }

  public cloneMetric(source: Metric, cloneOptions: IMetricCloneSelected): void {
    const payload: CloneResourceRequest = {
      type: CloneOrMoveResourceType.KeyResult,
      resourceId: source.id,
      targetType: CloneOrMoveResourceType.Objective,
      targetResourceId: cloneOptions.goal.id,
      resetProgress: !cloneOptions.progress,
      sendNotifications: cloneOptions.notifications,
      keepUpdatesInSync: cloneOptions.sync,
      cloneTasks: true,
      cloneTasksStatus: true,
      duplicateInsights: cloneOptions.duplicateInsight,
    };

    this.cloneInternal(payload, { targetGoalName: cloneOptions.goal.name });
  }

  public moveGoal(source: IGoal, selectedContext: IGoalMoveSelected): void {
    const selectedGoal = selectedContext.goal;
    const selectedSession = selectedContext.session;
    const resourceTargetType = selectedGoal ? CloneOrMoveResourceType.Objective : CloneOrMoveResourceType.Session;
    const targetId = selectedGoal ? selectedGoal.id : selectedSession.id;

    const moveRequest: MoveResourceRequest = {
      type: CloneOrMoveResourceType.Objective,
      resourceId: source.id,
      targetType: resourceTargetType,
      targetResourceId: targetId,
      targetSessionId: selectedSession.id,
      moveChildren: selectedContext.moveChildren,
    };

    this.moveInternal(moveRequest, selectedContext);
  }

  public moveMetric(metric: Metric, goal: IGoal): void {
    const oldGoalId = metric.goalId;
    // if there is an error the user will just not get the success modal
    this.metricsFacade
      .patch$(metric.id, { goalId: goal.id })
      .pipe(takeUntil(this.destroyService$))
      .subscribe({
        next: () => {
          this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { reason: "goalChanged" });
          this.broadcastService.emit("goalsChanged");
          const args = {
            reason: "metricMoved",
            detail: {
              goalId: oldGoalId,
              metric: { ...metric, goalId: goal.id },
            },
          };
          this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, args);

          const content = `"${metric.name}" ${localize("has_been_successfully")} ${localize("moved")} ${localize("in_preposition")} "${goal.name}" `;
          this.showSuccessNotification(localize("done"), content);
        },
        error: (error) => {
          this.handleError(error);
        },
      });
  }

  public moveInternal(request: MoveResourceRequest, selectedContext: IGoalMoveSelected): void {
    // create job item
    const inProgressNotification = this.notificationService.info(localize("moving_in_progress"), localize("moving_in_progress"), {
      notificationDuration: fiveMinutesInMilliseconds, // 5 min as this should be removed at some point
      notificationPlacement,
    });

    this.moveOkrsFacade
      .scheduleMoveJob$(request)
      .pipe(takeUntil(this.destroyService$))
      .subscribe({
        next: (jobId: JobId) => {
          /** Ping the move api every 3 seconds until the status is done or reverted. */
          const source = interval(this.intervalTime);
          const subscription = source.pipe(takeUntil(this.destroyService$)).subscribe(() => {
            this.moveOkrsFacade
              .fetchMoveJobById$(jobId)
              .pipe(takeUntil(this.destroyService$))
              .subscribe({
                next: (job) => {
                  const done = JobStatus.Done === job.status;
                  const reverted = JobStatus.Reverted == job.status || JobStatus.Reverting == job.status || JobStatus.ToBeReverted == job.status;

                  if (done) {
                    // remove in progress notification
                    this.notificationService.remove(inProgressNotification.messageId);
                    // stop the pinging
                    subscription.unsubscribe();

                    this.goalsFacade
                      .get$(job.rootMovedId)
                      .pipe(takeUntil(this.destroyService$))
                      .subscribe({
                        next: (newGoal) => {
                          const content = `"${newGoal.name}" ${localize("has_been_successfully")} ${localize("moved")} ${localize("in_preposition")} ${
                            selectedContext.session.title
                          }`;
                          this.broadcastService.emit("goalsChanged");
                          this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { reason: "goalChanged" });
                          const args = { reason: "goalMoved", detail: { goal: newGoal } };
                          this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { ...args });
                          this.showSuccessNotification(localize("done"), content);
                        },
                        error: (error) => {
                          this.handleError(error);
                        },
                      });
                  } else if (reverted) {
                    this.notificationService.remove(inProgressNotification.messageId);
                    subscription.unsubscribe();
                    this.showErrorNotification(localize("move_job_reverted_title"), localize("move_job_reverted_message"));
                  }
                },
                error: (error) => {
                  subscription.unsubscribe();
                  this.handleError(error);
                },
              });
          });
        },
        error: (error) => {
          this.handleError(error);
        },
      });
  }

  public cloneInternal(payload: CloneResourceRequest, notificationInfo?: NotificationInfo): void {
    const inProgressNotification = this.notificationService.info(localize("copying_in_progress"), localize("copying_in_progress"), {
      notificationDuration: fiveMinutesInMilliseconds, // 5 min
      notificationPlacement,
    });
    // Create job item
    this.cloneOkrFacade
      .scheduleCloneJob$(payload)
      .pipe(takeUntil(this.destroyService$))
      .subscribe({
        next: (cloneJobId: JobId) => {
          /** Ping the clone api every 3 seconds until the status is done or reverted. */
          const source = interval(this.intervalTime);
          const subscription = source.pipe(takeUntil(this.destroyService$)).subscribe(() => {
            this.cloneOkrFacade
              .fetchCloneJobById$(cloneJobId)
              .pipe(takeUntil(this.destroyService$))
              .subscribe({
                next: (cloneJob) => {
                  const done = JobStatus.Done === cloneJob.status;
                  const reverted = JobStatus.Reverted == cloneJob.status || JobStatus.Reverting == cloneJob.status || JobStatus.ToBeReverted == cloneJob.status;

                  if (done) {
                    this.notificationService.remove(inProgressNotification.messageId);
                    subscription.unsubscribe();
                    this.handleCloneSuccess(payload.type, cloneJob, notificationInfo);
                  } else if (reverted) {
                    this.notificationService.remove(inProgressNotification.messageId);
                    subscription.unsubscribe();
                    this.showErrorNotification(localize("clone_job_reverted_title"), localize("clone_job_reverted_message"));
                  }
                },
                error: (error) => {
                  subscription.unsubscribe();
                  this.handleError(error);
                },
              });
          });
        },
        error: (error) => {
          this.handleError(error);
        },
      });
  }

  public handleCloneSuccess(type: CloneOrMoveResourceType, cloneJob: ICloneJob, notificationInfo?: NotificationInfo): void {
    let content = "";
    let clonedItemsCount = 0;
    const clonedGoalsIds: string[] = [];
    switch (type) {
      case CloneOrMoveResourceType.Session:
        clonedItemsCount = Object.values(cloneJob.clonedIds).filter((item) => ["okr", "kr", "task"].includes(item)).length;
        content = `${clonedItemsCount} ${clonedItemsCount == 1 ? localize("item") : localize("items")}
            ${clonedItemsCount == 1 ? localize("has_been_successfully") : localize("have_been_successfully")} ${localize("copied")} ${localize("to")} ${
          notificationInfo.targetSessionName
        }`;
        this.showSuccessNotification(localize("done"), content);
        break;
      case CloneOrMoveResourceType.Objective:
        for (const goalId in cloneJob.clonedIds) {
          if (Object.prototype.hasOwnProperty.call(cloneJob.clonedIds, goalId) && cloneJob.clonedIds[goalId] === CloneOrMoveResourceType.Objective) {
            clonedGoalsIds.push(goalId);
          }
        }
        this.goalsFacade
          .getAll$({
            filter: {
              $and: [{ _id: { $in: clonedGoalsIds } }],
            },
            fields: [goalsFieldsV2],
          })
          .pipe(map((result) => result.items))
          .subscribe({
            next: (items) => {
              const rootClonedGoal = items.find((goal) => goal.id === cloneJob.rootClonedId);
              if (items.length > 1) {
                const args = { reason: "goalsCloned", detail: { goals: items } };
                this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { ...args });
              } else {
                const args = { reason: "goalCloned", detail: { goal: items[0] } };
                this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { ...args });
              }

              content = `"${rootClonedGoal.name}" ${localize("has_been_successfully")} ${localize("copied")} ${localize("in_preposition")} ${
                notificationInfo.targetSessionName
              }`;
              this.broadcastService.emit("goalsChanged");
              this.showSuccessNotification(localize("done"), content);
            },
            error: (error) => {
              this.handleError(error);
            },
          });
        break;
      case CloneOrMoveResourceType.KeyResult:
        this.metricsFacade
          .get$(cloneJob.rootClonedId)
          .pipe(takeUntil(this.destroyService$))
          .subscribe({
            next: (newMetric) => {
              this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { reason: "goalChanged" });
              this.broadcastService.emit("goalsChanged");
              const args = { reason: "metricCreated", detail: { goalId: newMetric.goalId, metric: newMetric } };
              this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { ...args });

              content = `"${newMetric.name}" ${localize("has_been_successfully")} ${localize("copied")} ${localize("to")} "${notificationInfo.targetGoalName}"`;
              this.showSuccessNotification(localize("done"), content);
            },
            error: (error) => {
              this.handleError(error);
            },
          });
        break;
    }
  }

  public handleError(err: IUIError): void {
    this.notificationService.remove();
    this.uiErrorHandlingService.handleModal(err);
  }

  public showSuccessNotification(title: string, content: string): void {
    this.notificationService.success(title, content, {
      notificationDuration: sixSecondsInMilliseconds,
      notificationPlacement,
    });
  }

  public showErrorNotification(title: string, content: string): void {
    this.notificationService.error(title, content, {
      notificationDuration: sixSecondsInMilliseconds,
      notificationPlacement,
    });
  }
}
